import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import Avatar from 'common/Avatar/Avatar'
import { extractProfileInfo, formatHandle } from 'components/ExternalIntegrationSelect/utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { getInitials } from 'utils'
import { Spinner } from 'common/Spinner/Spinner'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { EyeOpen, Send1, ArrowRotateLeft2, LinkAngled } from 'blaze-ui/icons/20'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { ValidationResult } from 'utils/validateScheduledPost'
import cx from 'classnames'
import Styles from './IntegrationSelector.module.scss'
import { getPostedRowAction, PostedRowAction, PostedRowActionType } from './getPostedRowAction'

interface PostedIntegrationRowProps {
  integration: ExternalIntegration
  target?: ScheduledPostTarget
  platform: ExternalIntegrationEnum
  needsReauth: boolean
  validation: ValidationResult
  onPublish: (targetId: number) => void
  onPublishWithoutTarget: () => void
  onViewPost: (url: string) => void
  onReauth: (popup?: Window) => void
}

interface ButtonPreset {
  icon: typeof EyeOpen
  label: string
  disabled?: boolean
  extraClassName?: string
}

const BUTTON_PRESETS: Partial<Record<PostedRowActionType, ButtonPreset>> = {
  'view-post': { icon: EyeOpen, label: 'View post' },
  'view-post-disabled': { icon: EyeOpen, label: 'View post', disabled: true },
  'post-now': { icon: Send1, label: 'Post Now' },
  retry: { icon: ArrowRotateLeft2, label: 'Retry', extraClassName: Styles.postedRetry },
  'retry-auth': { icon: LinkAngled, label: 'Reconnect' },
}

export const PostedIntegrationRow: React.FC<PostedIntegrationRowProps> = ({
  integration,
  target,
  platform,
  needsReauth,
  validation,
  onPublish,
  onPublishWithoutTarget,
  onViewPost,
  onReauth,
}) => {
  const { avatar, name, username, email } = extractProfileInfo(integration)
  const handle = formatHandle(username, email)

  const action = getPostedRowAction({
    platform,
    target,
    validation,
    hasTarget: !!target,
    needsReauth,
    onPublish,
    onPublishWithoutTarget,
    onViewPost,
  })

  return (
    <div className={Styles.postedRow} data-testid={`posted-row-${integration.id}`}>
      <div className={Styles.postedRowHeader}>
        <Avatar size={28} url={avatar || Silhouette} label={getInitials(name)} altText={name} />
        <div className={Styles.postedRowDetails}>
          <Text className={Styles.postedRowName} variant="smallList">
            {name}
          </Text>
          {handle && (
            <Text className={Styles.postedRowHandle} variant="metadata">
              {handle}
            </Text>
          )}
        </div>
      </div>
      {renderAction(action, integration, platform, onReauth)}
    </div>
  )
}

const renderAction = (
  action: PostedRowAction | null,
  integration: ExternalIntegration,
  platform: ExternalIntegrationEnum,
  onInitiatedOauth: (popup?: Window) => void,
) => {
  if (!action) return null

  if (action.type === 'posting' || action.type === 'loading') {
    return (
      <div className={Styles.postingLabel}>
        <Spinner />
        {action.type === 'posting' && <span>Posting…</span>}
      </div>
    )
  }

  const preset = BUTTON_PRESETS[action.type]
  if (!preset) return null

  const button = (
    <Button
      variant="secondary"
      size="md"
      fullWidth
      frontIcon={preset.icon}
      className={cx(Styles.postedActionButton, preset.extraClassName)}
      onPress={action.onPress}
      isDisabled={preset.disabled}
    >
      {preset.label}
    </Button>
  )

  if (action.type === 'retry-auth') {
    return (
      <InitiateOauthTrigger
        popUp
        provider={platform}
        accountToRefreshId={integration.id}
        onInitiatedOauth={onInitiatedOauth}
      >
        {button}
      </InitiateOauthTrigger>
    )
  }

  if (action.type === 'view-post-disabled') {
    return (
      <Tooltip label={action.tooltip || ''}>
        <div>{button}</div>
      </Tooltip>
    )
  }

  if (action.type === 'retry') {
    return (
      <Tooltip label={action.errorMessage || ''} hide={!action.errorMessage}>
        <div>{button}</div>
      </Tooltip>
    )
  }

  return button
}
